package com.partejar.app.feature.gestantes.data.remote

import com.partejar.app.core.network.ApiConfig
import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.feature.gestantes.data.GestanteListStage
import com.partejar.app.feature.gestantes.data.GestantesDataException
import com.partejar.app.feature.gestantes.data.GestantesDataSource
import com.partejar.app.feature.gestantes.data.GestanteClinicalDataSource
import com.partejar.app.feature.gestantes.data.GestanteClinicalDetail
import com.partejar.app.feature.gestantes.data.GestanteClinicalUpdate
import com.partejar.app.feature.gestantes.data.GestanteComorbidity
import com.partejar.app.feature.gestantes.data.GestanteExam
import com.partejar.app.feature.gestantes.data.GestanteExamInput
import com.partejar.app.feature.gestantes.data.GestanteExamResultInput
import com.partejar.app.feature.gestantes.data.GestanteExamType
import com.partejar.app.feature.gestantes.data.GestanteDeliveryInput
import com.partejar.app.feature.gestantes.data.GestantesPageData
import com.partejar.app.feature.gestantes.data.GestantesSessionExpiredException
import com.partejar.app.feature.gestantes.data.GestantesUnavailableException
import com.partejar.app.feature.gestantes.data.GestantesWorkspaceException
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.parameter
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.CancellationException

class GestantesRemoteDataSource(
    private val client: HttpClient,
    private val apiConfig: ApiConfig,
    private val currentWorkspaceRepository: CurrentWorkspaceRepository,
    private val mapper: GestantesRemoteMapper
) : GestantesDataSource, GestanteClinicalDataSource {
    override suspend fun loadPage(
        page: Int,
        size: Int,
        name: String?,
        statuses: Set<GestanteListStage>
    ): GestantesPageData {
        val workspaceId = currentWorkspaceRepository.currentWorkspaceId()
            ?: throw GestantesWorkspaceException()
        return try {
            val response = client.get(apiConfig.endpoint("/pacientes")) {
                header("X-Workspace-Id", workspaceId)
                parameter("page", page)
                parameter("size", size)
                name?.trim()?.takeIf(String::isNotEmpty)?.let { parameter("nome", it) }
                statuses.forEach { parameter("status", it.apiValue) }
            }
            when (response.status) {
                HttpStatusCode.OK -> mapper.map(response.body<GestantesPageDto>())
                HttpStatusCode.Unauthorized -> throw GestantesSessionExpiredException()
                HttpStatusCode.BadRequest,
                HttpStatusCode.Forbidden -> throw GestantesWorkspaceException()
                else -> throw GestantesUnavailableException()
            }
        } catch (error: CancellationException) {
            throw error
        } catch (error: GestantesDataException) {
            throw error
        } catch (error: Throwable) {
            throw GestantesUnavailableException(error)
        }
    }

    override suspend fun detail(id: Long): GestanteClinicalDetail = request {
        val response = client.get(apiConfig.endpoint("/pacientes/$id")) { workspaceHeader() }
        expect(response.status, HttpStatusCode.OK)
        mapper.mapDetail(response.body<GestanteDetailDto>())
    }

    override suspend fun update(id: Long, update: GestanteClinicalUpdate): GestanteClinicalDetail = request {
        val response = client.patch(apiConfig.endpoint("/pacientes/$id")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(GestanteClinicalUpdateDto(
                nomeBebe = update.babyName,
                enderecoCep = update.address.cep, enderecoLogradouro = update.address.street,
                enderecoNumero = update.address.number, enderecoComplemento = update.address.complement,
                enderecoBairro = update.address.neighborhood, enderecoCidade = update.address.city,
                enderecoUf = update.address.state,
                dataProvavelParto = update.dueDate,
                maternidadeEscolha = update.maternity,
                tipoPreNatal = update.prenatalType,
                moraEmBh = update.livesInBh
            ))
        }
        expect(response.status, HttpStatusCode.OK)
        mapper.mapDetail(response.body<GestanteDetailDto>())
    }

    override suspend fun registerDelivery(id: Long, input: GestanteDeliveryInput): GestanteClinicalDetail = request {
        val response = client.post(apiConfig.endpoint("/pacientes/$id/parto")) {
            workspaceHeader(); contentType(ContentType.Application.Json)
            setBody(RegisterGestanteDeliveryDto(input.date, input.time, input.route.apiValue, input.location, input.maternalOutcome.apiValue, input.neonatalOutcome.apiValue, input.observation))
        }
        expect(response.status, HttpStatusCode.Created)
        mapper.mapDetail(response.body<GestanteDetailDto>())
    }

    override suspend fun addComorbidity(id: Long, description: String, observation: String?): GestanteComorbidity = request {
        val response = client.post(apiConfig.endpoint("/pacientes/$id/comorbidades")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(CriarComorbidadeDto(description.trim(), observation))
        }
        expect(response.status, HttpStatusCode.Created)
        mapper.mapComorbidity(response.body<GestanteComorbidadeDto>())
    }

    override suspend fun updateComorbidity(id: Long, comorbidityId: Long, observation: String?): GestanteComorbidity = request {
        val response = client.patch(apiConfig.endpoint("/pacientes/$id/comorbidades/$comorbidityId")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(AtualizarComorbidadeDto(observation))
        }
        expect(response.status, HttpStatusCode.OK)
        mapper.mapComorbidity(response.body<GestanteComorbidadeDto>())
    }

    override suspend fun deactivateComorbidity(id: Long, comorbidityId: Long): GestanteComorbidity = request {
        val response = client.post(apiConfig.endpoint("/pacientes/$id/comorbidades/$comorbidityId/inativar")) {
            workspaceHeader()
        }
        expect(response.status, HttpStatusCode.OK)
        mapper.mapComorbidity(response.body<GestanteComorbidadeDto>())
    }

    override suspend fun examTypes(): List<GestanteExamType> = request {
        val response = client.get(apiConfig.endpoint("/exames/tipos")) { workspaceHeader() }
        expect(response.status, HttpStatusCode.OK)
        response.body<List<GestanteExamTypeDto>>().map(mapper::mapExamType)
    }

    override suspend fun examCatalog(): List<GestanteExamType> = request {
        val response = client.get(apiConfig.endpoint("/exames/catalogo")) { workspaceHeader() }
        expect(response.status, HttpStatusCode.OK)
        response.body<GestanteExamCatalogDto>().tipos.map(mapper::mapExamType)
    }

    override suspend fun exams(id: Long): List<GestanteExam> = request {
        val response = client.get(apiConfig.endpoint("/pacientes/$id/exames")) { workspaceHeader() }
        expect(response.status, HttpStatusCode.OK)
        response.body<List<GestanteExamDto>>().map(mapper::mapExam)
    }

    override suspend fun createExam(id: Long, input: GestanteExamInput): GestanteExam = request {
        val response = client.post(apiConfig.endpoint("/pacientes/$id/exames")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(input.toCreateDto())
        }
        expect(response.status, HttpStatusCode.Created)
        mapper.mapExam(response.body<GestanteExamDto>())
    }

    override suspend fun addExamResult(id: Long, examId: Long, input: GestanteExamResultInput): GestanteExam = request {
        val response = client.post(apiConfig.endpoint("/pacientes/$id/exames/$examId/resultados")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(input.toDto())
        }
        expect(response.status, HttpStatusCode.Created)
        mapper.mapExam(response.body<GestanteExamDto>())
    }

    override suspend fun updateExamResult(id: Long, examId: Long, resultId: Long, input: GestanteExamResultInput): GestanteExam = request {
        val response = client.patch(apiConfig.endpoint("/pacientes/$id/exames/$examId/resultados/$resultId")) {
            workspaceHeader()
            contentType(ContentType.Application.Json)
            setBody(input.toDto())
        }
        expect(response.status, HttpStatusCode.OK)
        mapper.mapExam(response.body<GestanteExamDto>())
    }

    private suspend fun <T> request(block: suspend () -> T): T = try {
        block()
    } catch (error: CancellationException) {
        throw error
    } catch (error: GestantesDataException) {
        throw error
    } catch (error: Throwable) {
        throw GestantesUnavailableException(error)
    }

    private fun io.ktor.client.request.HttpRequestBuilder.workspaceHeader() {
        val workspaceId = currentWorkspaceRepository.currentWorkspaceId()
            ?: throw GestantesWorkspaceException()
        header("X-Workspace-Id", workspaceId)
    }

    private fun expect(actual: HttpStatusCode, expected: HttpStatusCode) {
        if (actual == expected) return
        when (actual) {
            HttpStatusCode.Unauthorized -> throw GestantesSessionExpiredException()
            HttpStatusCode.BadRequest,
            HttpStatusCode.Forbidden -> throw GestantesWorkspaceException()
            else -> throw GestantesUnavailableException()
        }
    }

    private fun GestanteExamInput.toCreateDto() = CreateGestanteExamDto(
        exameTipoId = examTypeId,
        nomeExame = customName,
        realizadoEm = performedAt,
        observacao = observation,
        resultados = results.map { it.toDto() }
    )

    private fun GestanteExamResultInput.toDto() = GestanteExamResultInputDto(
        parametro = parameter,
        valor = value,
        unidade = unit,
        referencia = reference,
        status = status.apiValue,
        analiseId = analysisId,
        analiseUnidadeId = analysisUnitId,
        resultadoLivre = freeResult
    )
}
